/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VlcVehicleSystemsIndex.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import VehicleSystems from "../../../pages/labcar/VehicleSystems";
import VehicleComponents from "../../../pages/labcar/VehicleComponents";
import {
  adminSettingsAPI,
  HandleApiActions,
  labCar,
} from "../../../redux/actions";
import { selectVehicleSystemAction } from "../../../redux/actions/labCarActions";

function VlcVehicleSystemsIndex({
  drawerWidth = 240,
  drawerOpen = true,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  selectedZoneDetails,
  variantOptionList = [],
  vsAndVCOptionsList = [],
  getZoneVSCountList: getZoneVSCountListProp,
  viewArchived,
  selectedArchivedVSs,
  setSelectedArchivedVSs,
  selectedArchivedVCs,
  setSelectedArchivedVCs,
  clearAllCounter,
  retrievalRefreshCounter,
}) {
  const dispatch = useDispatch();

  const [selectedVehicleSystem, setSelectedVehicleSystem] = useState(null);

  const [filterState, setFilterState] = useState({
    sortField: "created_at",
    sortOrder: "ASC",
    nameFilter: [],
    assemblyNumFilter: [],
    descriptionFilter: [],
    nameFilterSearch: "",
    assemblyNumFilterSearch: "",
    descriptionFilterSearch: "",
  });

  const onFilterChange = (searchField, searchValue) => {
    setFilterState((prev) => ({
      ...prev,
      [searchField]: searchValue,
    }));
  };

  const onClearFilters = () => {
    setFilterState({
      sortField: "created_at",
      sortOrder: "ASC",
      nameFilter: [],
      assemblyNumFilter: [],
      descriptionFilter: [],
      nameFilterSearch: "",
      assemblyNumFilterSearch: "",
      descriptionFilterSearch: "",
    });
  };

  const getZoneVSCountList = useCallback(async () => {
    if (
      !selectedProjectDetails?.project_id ||
      !selectedSingleVariantDetails?.variant_id
    )
      return;
    if (typeof getZoneVSCountListProp === "function") {
      try {
        await getZoneVSCountListProp(viewArchived);
      } catch (e) {
        console.error(e);
      }
    }
    try {
      const apiDetails = labCar.get_vehicle_systems_all;
      await Promise.resolve(dispatch(
        HandleApiActions({
          ...apiDetails,
          show_toast: false,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            archive: !!viewArchived,
            page_number: 1,
            page_size: 10000,
          },
        }),
      ));
    } catch (e) {
      console.error(e);
    }
  }, [
    dispatch,
    getZoneVSCountListProp,
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
    viewArchived,
  ]);

  useEffect(() => {
    getZoneVSCountList().catch(console.error);
  }, [getZoneVSCountList]);

  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
  );
  const selectedVehicleSystemId = useSelector(
    (state) => state.labCarReducer.selectedVehicleSystemId,
  );
  const selectedVehicleSystemDetails = useSelector(
    (state) => state.labCarReducer.selectedVehicleSystemDetails,
  );
  const settingsVehicleSystemsDetails = useSelector(
    (state) => state.vehicleSystemsReducer,
  );

  const vehicleSystemsMasterListLength =
    settingsVehicleSystemsDetails?.vehicleSystemsDetails?.data?.length || 0;

  useEffect(() => {
    if (vehicleSystemsMasterListLength > 0) return;
    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_vehicle_systems,
        show_toast: false,
      }),
    );
  }, [dispatch, vehicleSystemsMasterListLength]);

  const supplierOptionsList = useMemo(() => {
    const suppliers = adminSettingsDetails?.adminSettings?.data?.ecu_suppliers;
    return Array.isArray(suppliers)
      ? suppliers.map((s) => ({ value: s?.name, label: s?.name }))
      : [];
  }, [adminSettingsDetails]);

  const vsOptionsList = useMemo(() => {
    if (Array.isArray(vsAndVCOptionsList) && vsAndVCOptionsList.length > 0) {
      return vsAndVCOptionsList;
    }
    const data = settingsVehicleSystemsDetails?.vehicleSystemsDetails?.data;
    if (Array.isArray(data)) {
      return data.map((vs) => ({
        value: vs?.name,
        label: vs?.name,
      }));
    }
    return [];
  }, [vsAndVCOptionsList, settingsVehicleSystemsDetails]);
  const vcOptionsList = useMemo(() => {
    if (!selectedVehicleSystemId) return [];

    const masterList =
      settingsVehicleSystemsDetails?.vehicleSystemsDetails?.data;
    const selectedName = selectedVehicleSystemDetails?.name;
    const matchedFromMaster =
      Array.isArray(masterList) && selectedName
        ? masterList.find((vs) => String(vs?.name) === String(selectedName))
        : null;

    const components =
      (Array.isArray(matchedFromMaster?.components)
        ? matchedFromMaster.components
        : null) ||
      (Array.isArray(selectedVehicleSystemDetails?.components)
        ? selectedVehicleSystemDetails.components
        : []);

    if (components.length === 0) {
      const grouped = Array.isArray(vsAndVCOptionsList)
        ? vsAndVCOptionsList
        : [];
      const matchedGrouped = selectedName
        ? grouped.find((vs) => String(vs?.label) === String(selectedName)) ||
        grouped.find((vs) => String(vs?.value) === String(selectedName))
        : null;
      const groupedComponents =
        matchedGrouped?.vehicleComponentsOptionsList ||
        matchedGrouped?.components ||
        [];
      return Array.isArray(groupedComponents) ? groupedComponents : [];
    }

    return components.map((vc) => ({ value: vc, label: vc }));
  }, [
    selectedVehicleSystemDetails,
    selectedVehicleSystemId,
    settingsVehicleSystemsDetails,
    vsAndVCOptionsList,
  ]);


  useEffect(() => {
    dispatch(selectVehicleSystemAction(null));
  }, [
    dispatch,
    selectedZoneDetails?.position,
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
  ]);

  return (
    <Box>
      <Box sx={{ flex: 2, display: "flex", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <VehicleSystems
            getZoneVSCountList={getZoneVSCountList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            selectedZoneDetails={selectedZoneDetails}
            variantOptionList={variantOptionList}
            supplierOptionsList={supplierOptionsList}
            vsOptionsList={vsOptionsList}
            filterState={filterState}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
            selectedVehicleSystem={selectedVehicleSystem}
            setSelectedVehicleSystem={setSelectedVehicleSystem}
            selectedArchivedVSs={selectedArchivedVSs}
            setSelectedArchivedVSs={setSelectedArchivedVSs}
            selectedArchivedVCs={selectedArchivedVCs}
            viewArchived={viewArchived}
            clearAllCounter={clearAllCounter}
            retrievalRefreshCounter={retrievalRefreshCounter}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <VehicleComponents
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            variantOptionList={variantOptionList}
            selectedZoneDetails={selectedZoneDetails}
            supplierOptionsList={supplierOptionsList}
            vcOptionsList={vcOptionsList}
            filterState={filterState}
            selectedArchivedVCs={selectedArchivedVCs}
            setSelectedArchivedVCs={setSelectedArchivedVCs}
            selectedArchivedVSs={selectedArchivedVSs}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
            selectedVehicleSystem={selectedVehicleSystem}
            viewArchived={viewArchived}
            clearAllCounter={clearAllCounter}
            retrievalRefreshCounter={retrievalRefreshCounter}
          />
        </Box>
      </Box>
    </Box>
  );
}


VlcVehicleSystemsIndex.propTypes = {
  drawerWidth: PropTypes.number,
  drawerOpen: PropTypes.bool,
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.any,
  }),
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.any,
  }),
  selectedZoneDetails: PropTypes.shape({
    position: PropTypes.any,
  }),
  variantOptionList: PropTypes.array,
  vsAndVCOptionsList: PropTypes.array,
  getZoneVSCountList: PropTypes.func,
  viewArchived: PropTypes.bool,
  selectedArchivedVSs: PropTypes.arrayOf(PropTypes.any),
  setSelectedArchivedVSs: PropTypes.func,
  selectedArchivedVCs: PropTypes.arrayOf(PropTypes.any),
  setSelectedArchivedVCs: PropTypes.func,
  clearAllCounter: PropTypes.number,
  retrievalRefreshCounter: PropTypes.number,
};

export default VlcVehicleSystemsIndex;
